import { useCallback, useEffect } from 'react';

import Image from '@/components/ui/Image';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/telemetry';

interface ErrorStateProps {
  title?: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

// The route the failure surfaced on — one shared component instruments every
// full-page failure without each caller passing a surface id.
function currentSurface(): string {
  return typeof window !== 'undefined' ? window.location.pathname : 'unknown';
}

export function ErrorState({
  title = 'Algo deu errado',
  message,
  actionLabel = 'Tentar novamente',
  onAction,
}: ErrorStateProps) {
  useEffect(() => {
    track('friction.load_failed', { surface: currentSurface() });
  }, []);

  const handleAction = useCallback(() => {
    track('friction.retry_clicked', { surface: currentSurface() });
    onAction?.();
  }, [onAction]);

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center gap-5 rounded-3xl border border-error-border bg-surface/80 px-6 py-10 text-center shadow-none">
      <Image
        src="/mascot/link-error.png"
        alt=""
        width={180}
        height={180}
        className="drop-shadow-[0_10px_30px_rgba(244,63,94,0.25)] select-none"
        priority
      />
      <div className="space-y-2">
        <h2 className="text-xl font-black tracking-tight text-text-primary">{title}</h2>
        <p className="max-w-md text-sm/6 text-text-secondary">{message}</p>
      </div>
      {onAction && <Button onClick={handleAction}>{actionLabel}</Button>}
    </div>
  );
}
